// The Trading pages' filters (SM-829). Whose activity: the whole subtree's
// users (the default, SM-862) or the node's own; and, with the User picker,
// one account. Picking
// one puts ?account= in the URL and the page then reads THAT account's
// route — the account in the PATH, because api-gateway strips
// broker_account_id from every query (SM-693). The history pages add a
// period. Every choice lives in the URL, so a filtered list survives a
// reload and can be shared.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Segmented } from "@/components/ui/Field";
import { useScope } from "@/components/layout/ScopeContext";
import { fetchAccount, fetchAccounts, keys } from "@/lib/queries";
import {
  HISTORY_DEFAULT_DAYS,
  HISTORY_MAX_DAYS,
  HISTORY_PRESET_DAYS,
  dayRangeWindow,
  lastDaysWindow,
  msToLocalDateInput,
  parseScopeSearch,
  presetOf,
  scopeToSearch,
  shortId,
  type HistoryWindow,
  type ScopeSearch,
} from "@/lib/trading";
import type { AccountScope } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

/** Rows per page on the Trading pages. */
export const TRADING_PAGE_LIMIT = 25;

/** The search params every Trading page keeps. */
export interface TradingSearch {
  /** Absent = the whole subtree (the default); "direct" = the node's own users. */
  scope?: ScopeSearch;
  account?: string;
  offset?: number;
}

export function validateTradingSearch(s: Record<string, unknown>): TradingSearch {
  return {
    scope: parseScopeSearch(s.scope),
    account: typeof s.account === "string" && s.account ? s.account : undefined,
    offset: typeof s.offset === "number" && s.offset > 0 ? s.offset : undefined,
  };
}

/** A history page's period, [from, to) in unix ms; absent = the last 30 days. */
export interface WindowSearch {
  from?: number;
  to?: number;
}

export function validateWindowSearch(s: Record<string, unknown>): WindowSearch {
  return {
    from: typeof s.from === "number" && s.from > 0 ? s.from : undefined,
    to: typeof s.to === "number" && s.to > 0 ? s.to : undefined,
  };
}

/** The scope switch and the User picker, plus a page's own controls. */
export function TradingFilters({
  scope,
  account,
  onScope,
  onAccount,
  children,
}: {
  scope: AccountScope;
  account: string | undefined;
  /** The ?scope= to write (undefined = the default, the whole subtree). */
  onScope: (scope: ScopeSearch | undefined) => void;
  onAccount: (account: string | undefined) => void;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <Segmented
        label={t("users.scope")}
        value={scope}
        options={[
          { value: "subtree", label: t("users.scopeSubtree") },
          { value: "direct", label: t("users.scopeDirect") },
        ]}
        onChange={(v) => onScope(scopeToSearch(v))}
      />
      <UserPicker scope={scope} value={account} onChange={onAccount} />
      {children}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/**
 * The User picker: searches the node's accounts (or the subtree's) by a
 * prefix of the user id or the broker account id, as the Users screen
 * does, and narrows the page to the one picked. A picked account shows its
 * user id and a way back to every user. The arrow keys walk from the input
 * through the results; Escape closes them.
 */
export function UserPicker({
  scope,
  value,
  onChange,
}: {
  scope: AccountScope;
  value: string | undefined;
  onChange: (account: string | undefined) => void;
}) {
  const { t } = useI18n();
  const { nodeId } = useScope();
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const query = useDebounced(q.trim(), 250);
  const params = { scope, q: query, limit: 8 };
  const resultsQ = useQuery({
    queryKey: keys.accounts(nodeId, params),
    queryFn: () => fetchAccounts(nodeId, params),
    enabled: !value && open && query !== "",
  });
  // The picked account, for its user id (the list read answers for it either way).
  const accountQ = useQuery({
    queryKey: keys.account(nodeId, value ?? ""),
    queryFn: () => fetchAccount(nodeId, value ?? ""),
    enabled: !!value,
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (accountId: string) => {
    setQ("");
    setOpen(false);
    onChange(accountId);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const options = [...(wrap.current?.querySelectorAll<HTMLButtonElement>("button[data-pick]") ?? [])];
    if (options.length === 0) return;
    e.preventDefault();
    const at = options.findIndex((o) => o === document.activeElement);
    const next = e.key === "ArrowDown" ? Math.min(at + 1, options.length - 1) : at - 1;
    if (next < 0) wrap.current?.querySelector<HTMLInputElement>("input")?.focus();
    else options[next]?.focus();
  };

  if (value) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-2xs font-semibold uppercase tracking-wide text-t-3">{t("trading.user")}</span>
        <div className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-line-2 bg-bg-2 pl-3 pr-1">
          <span className="mono min-w-0 truncate text-xs text-t-1" title={accountQ.data?.user_id ?? value}>
            {accountQ.data?.user_id ?? shortId(value)}
          </span>
          {accountQ.data && (
            <span className="mono shrink-0 text-2xs text-t-3" title={value}>
              {shortId(value)}
            </span>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange(undefined)}>
            {t("trading.userClear")}
          </Button>
        </div>
      </div>
    );
  }

  const items = resultsQ.data?.items ?? [];
  const showResults = open && query !== "";
  return (
    <div ref={wrap} className="relative flex min-w-0 flex-col gap-1.5" onKeyDown={onKeyDown}>
      <label htmlFor={`${id}-q`} className="text-2xs font-semibold uppercase tracking-wide text-t-3">
        {t("trading.user")}
      </label>
      <input
        id={`${id}-q`}
        type="search"
        value={q}
        autoComplete="off"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t("users.searchPlaceholder")}
        aria-controls={showResults ? `${id}-results` : undefined}
        className="h-10 w-64 max-w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
      />
      {showResults && (
        <div
          id={`${id}-results`}
          role="region"
          aria-label={t("trading.userResults")}
          className="absolute left-0 top-full z-30 mt-1 w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-line-2 bg-bg-1 p-1 shadow-lg"
        >
          {resultsQ.isLoading ? (
            <p className="px-2 py-1.5 text-xs text-t-3">{t("common.loading")}</p>
          ) : resultsQ.error ? (
            <p className="px-2 py-1.5 text-xs text-no">{t("trading.userSearchFailed")}</p>
          ) : items.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-t-3">{t("users.empty")}</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {items.map((a) => (
                <li key={a.broker_account_id}>
                  <button
                    type="button"
                    data-pick
                    onClick={() => pick(a.broker_account_id)}
                    className="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left hover:bg-bg-3 focus:bg-bg-3 focus:outline-none"
                  >
                    <span className="mono text-xs text-t-1">{a.user_id}</span>
                    <span className="mono text-2xs text-t-3" title={a.broker_account_id}>
                      {shortId(a.broker_account_id)} · {a.broker_id === nodeId ? t("users.thisNode") : t("positions.node", { id: shortId(a.broker_id) })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A history page's period: the last 7, 30 or 90 days, or a range of whole
 * local days (the end day included) of at most 92. The caller keys it by
 * the window, so the inputs follow the URL. Choosing the default (30 days)
 * hands back undefined, which leaves the URL clean.
 */
export function WindowPicker({
  window: w,
  now,
  onChange,
}: {
  window: HistoryWindow;
  now: number;
  onChange: (w: HistoryWindow | undefined) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [fromDay, setFromDay] = useState(msToLocalDateInput(w.from));
  const [toDay, setToDay] = useState(msToLocalDateInput(w.to - 1));
  const [error, setError] = useState<string | null>(null);
  const preset = presetOf(w, now);
  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-3">
        <Segmented<string>
          label={t("trading.period")}
          value={preset ? String(preset) : ""}
          options={HISTORY_PRESET_DAYS.map((d) => ({ value: String(d), label: t("trading.lastDays", { days: d }) }))}
          onChange={(v) => onChange(Number(v) === HISTORY_DEFAULT_DAYS ? undefined : lastDaysWindow(Number(v), now))}
        />
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const r = dayRangeWindow(fromDay, toDay);
            if (!r.ok) {
              setError(r.error === "tooLong" ? t("trading.rangeTooLong", { days: HISTORY_MAX_DAYS }) : t("statements.badRange"));
              return;
            }
            setError(null);
            onChange(r.window);
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-from`} className="text-2xs font-semibold uppercase tracking-wide text-t-3">
              {t("statements.fromDay")}
            </label>
            <input
              id={`${id}-from`}
              type="date"
              value={fromDay}
              onChange={(e) => setFromDay(e.target.value)}
              className="h-10 rounded-md border border-line-1 bg-bg-2 px-2 text-sm text-t-1"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${id}-to`} className="text-2xs font-semibold uppercase tracking-wide text-t-3">
              {t("statements.toDay")}
            </label>
            <input
              id={`${id}-to`}
              type="date"
              value={toDay}
              onChange={(e) => setToDay(e.target.value)}
              className="h-10 rounded-md border border-line-1 bg-bg-2 px-2 text-sm text-t-1"
            />
          </div>
          <Button type="submit" variant="ghost">
            {t("statements.apply")}
          </Button>
        </form>
      </div>
      {error && (
        <p className="text-2xs text-no" role="alert">
          {error}
        </p>
      )}
      <p className="text-2xs text-t-3">{t("trading.window", { from: msToLocalDateInput(w.from), to: msToLocalDateInput(w.to - 1) })}</p>
    </div>
  );
}
